const ENV = require("../_env");
const g = (window[ENV.projectName] = window[ENV.projectName] || {});

import { vec2, vec3, mat4 } from "gl-matrix";

import Renderer from "./_unshiftWebGL/renderers/Renderer";
import PerspectiveCamera from "./_unshiftWebGL/cameras/PerspectiveCamera";
import Object3D from "./_unshiftWebGL/core/Object3D";
import Color from "./_unshiftWebGL/math/Color";
import PlaneGeometry from "./_unshiftWebGL/geometries/PlaneGeometry";
import InstancedGeometryBuilder from "./_unshiftWebGL/core/InstancedGeometryBuilder";
import Texture from "./_unshiftWebGL/textures/Texture";
import RenderTarget from "./_unshiftWebGL/renderers/RenderTarget";
import Mesh from "./_unshiftWebGL/objects/Mesh";

import SimplexNoise from "simplex-noise";

import Planes from "./Planes";
import Material from "./_unshiftWebGL/core/Material";

const FOV = 45;
const GRID_SIZE = 15;
const BASE_WIDTH = { s: 750, l: 1440 };
const BASE_HEIGHT = { s: 1280, l: 720 };

const BLOCK_NOISE_TEXTURE_SIZE = 256;

export default Vue.extend({
  data() {
    return {
      width: 0,
      height: 0,
      animationId: null,

      canvas: null,
      renderer: null,
      gl: null,
      camera: null,
      rootObject: null,
      isNoWebGL: true,
      isInited: false,
      devicePixelRatio: 1,
      startTime: 0,
      texturesData: {},
      layoutMode: "",
      scaleY: 1,

      numCols: { s: 1, l: 1 },
      numRows: { s: 1, l: 1 }
    };
  },

  computed: {
    classObj() {
      return {
        "is-noWebGL": this.isNoWebGL,
        "is-layoutModeS": this.$store.state.layoutMode == "s"
      };
    }
  },

  mounted() {
    this.canvas = this.$refs.canvas;
    this.update = this.update.bind(this);
  },

  methods: {
    async init(texturesData, layoutMode) {
      if (this.isInited) return;

      this.texturesData = texturesData;
      // this.layoutMode = layoutMode;
      this.layoutMode = "l";

      await this.initWebGL().catch(() => {
        this.isNoWebGL = true;
      });

      this.onResize();
      await Vue.nextTick();
    },

    async initWebGL() {
      this.isInited = true;

      return new Promise(async (resolve, reject) => {
        this.width = this.$el.offsetWidth;
        this.height = this.$el.offsetHeight;

        //
        // renderer
        //
        this.devicePixelRatio = window.devicePixelRatio;
        this.renderer = new Renderer({
          canvas: this.canvas,
          devicePixelRatio: this.devicePixelRatio,
          antialias: true
        });
        this.renderer.setBgColor(new Color(1, 1, 1), 1);
        this.gl = this.renderer.getGLContext();

        if (!this.gl) {
          this.isNoWebGL = true;
          reject();
          return;
        }

        this.renderTarget = new RenderTarget(this.gl, 128, 128, {
          wrapS: "MIRRORED_REPEAT",
          wrapT: "MIRRORED_REPEAT"
        });

        //
        // camera
        //
        this.camera = new PerspectiveCamera(
          (FOV / 180) * Math.PI,
          1,
          1,
          100000
        );
        this.cameraDefaultPos = vec3.clone(this.camera.position);
        this.camera.lookAt(0, 0, 0);

        // geometry
        const baseGeometry = new PlaneGeometry(GRID_SIZE, GRID_SIZE, 1, 1);
        // 750: 1268 (15)  50 * 85 = 4250
        // 1280: 720 (15) 86 * 48 = 4128
        this.numCols = {
          s: Math.ceil(BASE_WIDTH.s / GRID_SIZE),
          l: Math.ceil(BASE_WIDTH.l / GRID_SIZE)
        };
        this.numRows = {
          s: Math.ceil(BASE_HEIGHT.s / GRID_SIZE),
          l: Math.ceil(BASE_HEIGHT.l / GRID_SIZE)
        };
        const numInstance = {
          s: this.numCols.s * this.numRows.s,
          l: this.numCols.l * this.numRows.l
        };

        const bufferGeometryBuilders = {
          s: new InstancedGeometryBuilder(baseGeometry, numInstance.s, this.gl),
          l: new InstancedGeometryBuilder(baseGeometry, numInstance.l, this.gl)
        };
        this.initBufferGeometry(
          bufferGeometryBuilders.s,
          GRID_SIZE,
          this.numCols.s,
          this.numRows.s
        );
        this.initBufferGeometry(
          bufferGeometryBuilders.l,
          GRID_SIZE,
          this.numCols.l,
          this.numRows.l
        );

        let colIndicesS = [];
        let rowIndicesS = [];
        for (let i = 0; i < numInstance.s; i++) {
          colIndicesS.push(i % this.numCols.s);
          rowIndicesS.push(Math.floor(i / this.numCols.s));
        }
        bufferGeometryBuilders.s.setAttribute(
          "colIndex",
          colIndicesS,
          Float32Array,
          1,
          1
        );
        bufferGeometryBuilders.s.setAttribute(
          "rowIndex",
          rowIndicesS,
          Float32Array,
          1,
          1
        );

        let colIndicesL = [];
        let rowIndicesL = [];
        for (let i = 0; i < numInstance.l; i++) {
          colIndicesL.push(i % this.numCols.l);
          rowIndicesL.push(Math.floor(i / this.numCols.l));
        }
        bufferGeometryBuilders.l.setAttribute(
          "colIndex",
          colIndicesL,
          Float32Array,
          1,
          1
        );
        bufferGeometryBuilders.l.setAttribute(
          "rowIndex",
          rowIndicesL,
          Float32Array,
          1,
          1
        );

        // blockNoiseTexture
        this.blockNoiseCanvas = document.createElement("canvas");
        this.blockNoiseCanvas.width = BLOCK_NOISE_TEXTURE_SIZE;
        this.blockNoiseCanvas.height = BLOCK_NOISE_TEXTURE_SIZE;
        this.blockNoiseCtx = this.blockNoiseCanvas.getContext("2d");
        this.blockNoiseTexture = new Texture();
        this.blockNoiseTexture.minFilter = "NEAREST";
        this.blockNoiseTexture.magFilter = "NEAREST";
        this.blockNoiseTexture.wrapS = "MIRRORED_REPEAT";
        this.blockNoiseTexture.wrapT = "MIRRORED_REPEAT";
        this.blockNoiseTexture.setCanvas(this.blockNoiseCanvas);
        this.blockNoiseTexture.setGLContext(this.gl);

        // document.body.appendChild(this.blockNoiseCanvas);
        // this.blockNoiseCanvas.style.top = "0";
        // this.blockNoiseCanvas.style.left = "0";
        // this.blockNoiseCanvas.style.position = "fixed";
        // this.blockNoiseCanvas.style.zIndex = "50000";

        // planes
        this.planes1 = new Planes(
          this.gl,
          baseGeometry,
          this.camera,
          1,
          this.blockNoiseTexture
        );
        this.planes2 = new Planes(
          this.gl,
          baseGeometry,
          this.camera,
          -1,
          this.blockNoiseTexture
        );
        this.planes2.material.uniforms.color.value = [
          255 / 255,
          221 / 255,
          196 / 255
        ];

        this.renderMaterial = new Material({
          vertexShader: require("./_glsl/render.vert"),
          fragmentShader: require("./_glsl/render.frag"),
          depthTest: false,
          uniforms: {
            resolution: { type: "uniform2f", value: [0, 0] },
            glitchTex: { type: "texture", value: this.blockNoiseTexture },
            glitchValue: { type: "uniform1f", value: 0 },
            texture: { type: "texture", value: this.renderTarget }
          }
        });
        this.renderPlane = new Mesh(baseGeometry, this.renderMaterial);

        // rootObject
        this.rootObject = new Object3D();

        await Promise.all([
          this.planes1.init(
            this.texturesData,
            "img1",
            bufferGeometryBuilders,
            this.layoutMode
          ),
          this.planes2.init(
            this.texturesData,
            "img2",
            bufferGeometryBuilders,
            this.layoutMode
          )
        ]);

        this.scaleY = BASE_HEIGHT.s / (this.numRows.s * GRID_SIZE);
        this.planes1.material.uniforms.scaleY.value = this.scaleY;
        this.planes2.material.uniforms.scaleY.value = this.scaleY;

        this.rootObject.add(this.planes1);
        this.rootObject.add(this.planes2);
        this.rootObject.add(this.renderPlane);

        this.rootObject.setGLContext(this.gl);
        this.rootObject.updateTextureUnitIndicies();
        this.rootObject.updateMatrix();

        // this.initDatGUI();

        this.onResize();

        let v = 0;
        const doTween = () => {
          const toV = v === 0 ? 1 : 0;
          v = (v + 1) % 2;
          TweenMax.to(this.planes1.material.uniforms.animationValue, 3, {
            value: toV,
            overwrite: true,
            ease: Linear.easeNone,
            onUpdate: () => {
              this.planes2.material.uniforms.animationValue.value = this.planes1.material.uniforms.animationValue.value;
              this.update();
            }
          });
        };

        document.addEventListener("click", () => {
          doTween();
        });
        document.addEventListener("keydown", () => {
          doTween();
        });

        console.log("webgl inited");
        this.isNoWebGL = false;
        resolve();
      }).catch(error => {
        this.isNoWebGL = true;
        console.error(error);
      });
    },

    initBufferGeometry(bufferGeometryBuilder, size, numCols, numRows) {
      const noiseGenerator = new SimplexNoise();
      const offset0 = [];
      const noiseValues = [];

      let indexOffset = 0;
      const offsetX = -size * numCols * 0.5 + size * 0.5;
      const offsetY = size * this.scaleY * numRows * 0.5 - size * 0.5;
      let x, y;
      for (let j = 0; j < numRows; j++) {
        for (let i = 0; i < numCols; i++) {
          indexOffset = numCols * j + i;
          x = offsetX + size * i;
          y = offsetY - size * this.scaleY * j;
          offset0[indexOffset * 3 + 0] = x;
          offset0[indexOffset * 3 + 1] = y;
          offset0[indexOffset * 3 + 2] = 0;
          noiseValues.push(
            (noiseGenerator.noise2D(
              (x / BASE_WIDTH.s) * 3,
              (y / BASE_WIDTH.s) * 3
            ) +
              1) *
              0.5
          );
          noiseValues.push(noiseGenerator.noise2D(y, x));
        }
      }
      bufferGeometryBuilder.setAttribute(
        "offset0",
        offset0,
        Float32Array,
        3,
        1
      );
      bufferGeometryBuilder.setAttribute(
        "noiseValues",
        noiseValues,
        Float32Array,
        2,
        1
      );
    },

    initDatGUI() {
      const dat = require("dat.gui");
      this.gui = new dat.GUI();
      this.gui.domElement.parentElement.style.zIndex = 1000;

      this.animationValue = this.planes1.material.uniforms.animationValue.value;
      this.gui
        .add(this, "animationValue", 0, 1, this.animationValue)
        .step(0.01)
        .onChange(value => {
          this.planes1.material.uniforms.animationValue.value = value;
          this.planes2.material.uniforms.animationValue.value = value;
          this.update();
        });

      this.glitchValue = this.planes1.material.uniforms.glitchValue.value;
      this.gui
        .add(this, "glitchValue", 0, 1, this.glitchValue)
        .step(0.01)
        .onChange(value => {
          this.planes1.material.uniforms.glitchValue.value = value;
          this.planes2.material.uniforms.glitchValue.value = value;
          this.renderMaterial.uniforms.glitchValue.value = value;
          this.update();
        });

      this.gui.add(this, "updateBlockNoise");
      return;
    },

    update() {
      // this.animationId = requestAnimationFrame(this.update);
      this.renderPlane.isVisible = false;
      this.planes1.isVisible = true;
      this.planes2.isVisible = true;
      this.renderer.render(this.rootObject, this.renderTarget);

      this.renderPlane.isVisible = true;
      this.planes1.isVisible = false;
      this.planes2.isVisible = false;
      this.renderer.render(this.rootObject);
    },

    glitch(min = 6, max = 12, value = 1) {
      let glicthTime = 0;

      const timeline = new TimelineMax();
      for (
        let i = 0, l = min + Math.floor(Math.random() * (max - min));
        i < l;
        i++
      ) {
        timeline.add(() => {
          this.renderMaterial.uniforms.glitchValue.value =
            Math.random() * value * 2 - value;
          this.planes1.material.uniforms.glitchValue.value =
            Math.random() * value * 2 - value;
          this.planes2.material.uniforms.glitchValue.value =
            Math.random() * value * 2 - value;
          this.updateBlockNoise();
        }, glicthTime);
        glicthTime += 0.02 + Math.floor(Math.random() * 0.4);
      }
      timeline.add(() => {
        this.renderMaterial.uniforms.glitchValue.value = 0;
        this.planes1.material.uniforms.glitchValue.value = 0;
        this.planes2.material.uniforms.glitchValue.value = 0;
      }, glicthTime);
      return timeline;
    },

    setMode(isAnother) {
      if (this.isNoWebGL || !this.isInited) return null;

      return new Promise(resolve => {
        this.startTime = new Date().getTime() + Math.random() * 40000;

        this.timeline = new TimelineMax({
          onUpdate: () => {
            this.planes2.material.uniforms.animationValue.value = this.planes1.material.uniforms.animationValue.value;
            this.update();
          },
          onComplete: () => {
            resolve();
          }
        });

        const t1 = 0.2;
        const t2 = Math.random() * 0.2;
        const t3 = Math.random() * 0.04;

        this.timeline;
        // .add(this.glitch(6, 12, 0.6), t1)
        // .add(this.glitch(6, 12, 0.6), `+=${t2}`)
        // .add(this.glitch(6, 12, 0.6), `+=${t3}`);

        this.timeline.to(this.planes1.material.uniforms.animationValue, 3, {
          value: isAnother ? 1 : 0,
          delay: 0,
          ease: Linear.easeNone
        });
        // .add(this.glitch(3, 4, 0.4));
      });
    },

    updateVPMatris() {
      this.vpMatrix = mat4.create();
      mat4.multiply(
        this.vpMatrix,
        this.camera.projectionMatrix,
        this.camera.viewMatrix
      );
    },

    updateBlockNoise() {
      let c1, c2, w, h, offsetX, offsetY;
      this.blockNoiseCtx.clearRect(
        0,
        0,
        BLOCK_NOISE_TEXTURE_SIZE,
        BLOCK_NOISE_TEXTURE_SIZE
      );

      for (let i = 0, l = 10 + Math.floor(Math.random() * 20); i < l; i++) {
        c1 = Math.random();
        c2 = Math.random();
        offsetX = Math.random() * BLOCK_NOISE_TEXTURE_SIZE;
        offsetY = Math.random() * BLOCK_NOISE_TEXTURE_SIZE;
        w = (0.4 + Math.random() * 0.4) * BLOCK_NOISE_TEXTURE_SIZE;
        h = (0.04 + Math.random() * 0.04) * BLOCK_NOISE_TEXTURE_SIZE;
        this.blockNoiseCtx.fillStyle = `rgba(${Math.floor(
          c1 * 256
        )}, ${Math.floor(c2 * 256)}, 0, 1)`;
        this.blockNoiseCtx.fillRect(offsetX - w / 2, offsetY - h / 2, w, h);
      }

      this.blockNoiseTexture.updateGLTexture();
    },

    async onResize(layoutMode = "") {
      if (this.isNoWebGL || !this.isInited) return;

      this.width = this.$el.offsetWidth;
      this.height = this.$el.offsetHeight;
      const aspectRatio = this.width / this.height;

      this.renderer.setSize(this.width, this.height);

      this.renderTarget.setSize(
        this.width * this.devicePixelRatio,
        this.height * this.devicePixelRatio
      );
      this.renderMaterial.uniforms.resolution.value[0] =
        this.width * this.devicePixelRatio;
      this.renderMaterial.uniforms.resolution.value[1] =
        this.height * this.devicePixelRatio;

      this.camera.aspect = aspectRatio;
      this.camera.position[2] =
        this.height / Math.tan((FOV * Math.PI) / 360) / 2;
      this.camera.lookAt(0, 0, 0);
      this.camera.updateProjectionMatrix();
      this.updateVPMatris();

      let scale =
        this.layoutMode == "l"
          ? this.height / BASE_HEIGHT.l
          : this.width / BASE_WIDTH.s;
      const offsetY = 0;
      const numCols = this.numCols[this.layoutMode];
      const numRows = this.numRows[this.layoutMode];
      this.planes1.onResize(
        layoutMode,
        this.width,
        this.height,
        this.devicePixelRatio,
        this.vpMatrix,
        scale,
        offsetY,
        numCols,
        numRows
      );
      this.planes2.onResize(
        layoutMode,
        this.width,
        this.height,
        this.devicePixelRatio,
        this.vpMatrix,
        scale,
        offsetY,
        numCols,
        numRows
      );

      if (this.planes1.isUpdated) {
        this.planes1.isUpdated = false;
        this.planes2.isUpdated = false;
        this.rootObject.updateMatrix();
        this.rootObject.updateTextureUnitIndicies();
      }

      this.update();
    }
  }
});
